import { Notice } from '@/components/ui/Notice';
import type { ActualsState } from '@/lib/use-team-performance';

/**
 * Whether the figures can be computed at all, and how — in the server's words.
 *
 * ⚠️ **`reason` is printed VERBATIM.** It is composed in `unattributableReason()`
 * server-side, and shortening it here would put this app back in the business of
 * knowing which groups attribute — a copy of the server's own
 * `RELATIONSHIP_BY_GROUP_SLUG` in a place nothing keeps in step.
 *
 * ⚠️ **An unrecognised `kind` says NOTHING** rather than falling back to a
 * default that reads as an answer. Unknown means the contract is newer than this
 * build — the same rule as `xero.ts`'s refusal map.
 *
 * ⚠️ **It lives in its own file rather than beside the tab's other shared
 * pieces**, so `financial-shared.ts` can stay a plain `.ts` module. A module
 * exporting a component alongside constants trips `react(only-export-components)`
 * once per constant — ten warnings for one component, none of which say anything
 * about this component.
 */
export function AttributionNote({ state }: { state: ActualsState }) {
  const attribution = state.attribution;
  if (attribution === null) return null;

  if (attribution.kind === 'unattributable') {
    return (
      <Notice tone="warning">
        <span className="break-words">{attribution.reason}</span>
      </Notice>
    );
  }

  /*
   * ⚠️ **ONLY THE SERVER'S OWN SENTENCE IS RENDERED — owner's ruling,
   * 2026-09-05.** The `group` branch said "Measured through {via}." in this
   * app's words; that was an explanation rather than a refusal and went with the
   * rest of the helper text. `attribution.reason` above stays because it is the
   * server's text, verbatim.
   */
  return null;
}
